import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Maximize2, Minimize2, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import { onboardingService } from '../services/onboardingService';
import { workflowService } from '../services/workflowService';
import { DynamicForm, GridInputForm, PdfSurveyForm } from '../form-builder';
import type {
  ExistingSubmissionQuery,
  FormField,
  FormSchema,
  FormSubmitValues,
  FormUploadOptions,
  GridSubmitPayload,
  PdfSurveySubmitPayload,
} from '../form-builder';
import type { WorkflowNextEvent } from '../types/workflow';

interface WorkflowEventModalProps {
  entityId: number | string;
  event: WorkflowNextEvent;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Walks the `_lookup` group in the schema and produces the body keys the server
 * expects to be filled by the client (per API doc §5.2 / §5.3 — `entity_id`,
 * `filled_by`, etc.). Visible field values are still authored by the user via
 * `DynamicForm`; this hook only adds lookup-derived keys on top.
 */
const buildLookupAdditions = (
  schema: FormSchema,
  entityId: number | string,
  currentUser: Record<string, unknown> | null,
): Record<string, unknown> => {
  const additions: Record<string, unknown> = {};
  const lookupGroup = schema.columns.find((g) => g.title === '_lookup');
  if (!lookupGroup) return additions;

  for (const item of lookupGroup.items as FormField[]) {
    const lookupKey = (item as any).lookup_key;
    const lookupType = (item as any).lookup_type;
    if (lookupType !== 'local' || typeof lookupKey !== 'string') continue;

    if (lookupKey.startsWith('user.')) {
      // user.<prop> → body[item.title] = currentUser[<prop>]
      const prop = lookupKey.slice('user.'.length);
      const value = currentUser ? (currentUser as Record<string, unknown>)[prop] : undefined;
      if (value !== undefined && value !== null && value !== '') {
        additions[item.title] = value;
      }
    } else if (lookupKey.startsWith('body.')) {
      // body.<bodyKey> → body[<bodyKey>] = entityId.
      // (The metadata's `title` here describes the server-side field that
      // gets derived from the body value, not a body key itself.)
      const bodyKey = lookupKey.slice('body.'.length);
      additions[bodyKey] = entityId;
    }
  }
  return additions;
};

export const WorkflowEventModal = ({
  entityId,
  event,
  isOpen,
  onClose,
  onSuccess,
}: WorkflowEventModalProps) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [serverError, setServerError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  const metadataQuery = useQuery({
    queryKey: ['workflow-event-meta', entityId, event.event],
    queryFn: () => workflowService.getEventMetadata(entityId, event.event),
    enabled: isOpen,
    staleTime: 60 * 1000,
  });

  const submitMutation = useMutation({
    mutationFn: (payload: FormSubmitValues) => workflowService.submitEvent(entityId, event.event, payload),
    onSuccess: (response) => {
      showSuccess(
        event.display_title || 'Action Completed',
        response.message || `Workflow event "${event.event}" submitted successfully.`,
      );
      onSuccess();
    },
    onError: (err: any) => {
      const message = err?.message || 'Failed to submit the action.';
      setServerError(message);
      showError('Action Failed', message);
    },
  });

  // Reset transient state whenever a different event modal is opened.
  useEffect(() => {
    if (isOpen) setServerError(null);
  }, [isOpen, event.event]);

  const lookupGroupHasIncompleteUser = useMemo(() => {
    if (!metadataQuery.data) return false;
    const lookupGroup = metadataQuery.data.columns.find((g) => g.title === '_lookup');
    if (!lookupGroup) return false;
    return lookupGroup.items.some((item: any) => {
      if (item.lookup_type !== 'local') return false;
      if (typeof item.lookup_key !== 'string') return false;
      if (!item.lookup_key.startsWith('user.')) return false;
      const prop = item.lookup_key.slice('user.'.length);
      const value = user ? (user as unknown as Record<string, unknown>)[prop] : undefined;
      return item.rqd && (value === undefined || value === null || value === '');
    });
  }, [metadataQuery.data, user]);

  if (!isOpen) return null;

  const handleSubmit = (visiblePayload: FormSubmitValues) => {
    if (!metadataQuery.data) return;
    setServerError(null);
    const userRecord = user ? (user as unknown as Record<string, unknown>) : null;
    const additions = buildLookupAdditions(metadataQuery.data, entityId, userRecord);
    submitMutation.mutate({ ...visiblePayload, ...additions });
  };

  // Versioned-document uploads (PAN / Aadhaar / etc.) go through the
  // entity-aware Document API (see docs/onboarding/API/Document-Upload.md).
  // The backoffice user is authenticated via FusionAuth JWT, so the server
  // records `uploaded_by_source = "BACKOFFICE"`.
  const uploadFile = useCallback(
    (file: File, options?: FormUploadOptions) => {
      if (typeof options?.documentTypeId !== 'number') {
        return Promise.reject(
          new Error(
            'This field is missing its document_type_id; the upload endpoint requires it.',
          ),
        );
      }
      return onboardingService.uploadDocument({
        entityType: 'case',
        entityId: entityId,
        documentTypeId: options.documentTypeId,
        file,
        signal: options.signal,
      });
    },
    [entityId],
  );

  const fetchDocumentVersion = useCallback(
    (versionId: number, options?: { signal?: AbortSignal }) =>
      onboardingService.getDocumentVersion({
        entityType: 'case',
        entityId: entityId,
        versionId,
        signal: options?.signal,
      }),
    [entityId],
  );

  // Existing-submission prefill: workflow forms typically declare
  // `identifier: ["entity_id"]`, which the modal supplies via
  // `externalIdentifiers` below. The fetcher binds to the event's form name.
  const eventFormName = metadataQuery.data?.name;
  const existingSubmissionFetcher = useMemo(() => {
    if (!eventFormName) return undefined;
    return (query: ExistingSubmissionQuery) =>
      onboardingService.getExistingSubmission(eventFormName, query);
  }, [eventFormName]);
  const externalIdentifiers = useMemo(
    () => ({ entity_id: entityId }),
    [entityId],
  );

  // PDF-survey and grid_input layouts both need more horizontal room than
  // the default linear form, so we widen those. Fullscreen toggle is also
  // available on the wider layouts.
  const isPdfSurvey = metadataQuery.data?.resource_type === 'pdf_survey';
  const isGridInput = metadataQuery.data?.resource_type === 'grid_input';
  const wantsWideLayout = isPdfSurvey || isGridInput;
  const widthClass = fullscreen
    ? 'w-screen h-screen max-w-none rounded-none'
    : wantsWideLayout
    ? 'w-full max-w-6xl max-h-[95vh]'
    : 'w-full max-w-3xl max-h-[90vh]';

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${
        fullscreen ? '' : 'p-4'
      }`}
    >
      <div className={`bg-white rounded-xl shadow-xl overflow-hidden flex flex-col ${widthClass}`}>
        <div className="flex items-center justify-between border-b border-rgf-gray-200 px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-rgf-gray-900 truncate">
              {event.display_title || event.event}
            </h2>
            {event.resource_name && (
              <p className="text-xs text-rgf-gray-500 font-mono">{event.resource_name}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            {wantsWideLayout && (
              <button
                type="button"
                aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                className="p-1 text-rgf-gray-400 hover:text-rgf-gray-700"
                onClick={() => setFullscreen((v) => !v)}
              >
                {fullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
              </button>
            )}
            <button
              type="button"
              aria-label="Close"
              className="p-1 text-rgf-gray-400 hover:text-rgf-gray-700"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          {metadataQuery.isLoading ? (
            <div className="text-center text-rgf-gray-500 py-12">Loading form…</div>
          ) : metadataQuery.error ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {(metadataQuery.error as Error)?.message || 'Failed to load the action form.'}
              </div>
              <Button onClick={() => metadataQuery.refetch()}>Try Again</Button>
            </div>
          ) : metadataQuery.data ? (
            <>
              {lookupGroupHasIncompleteUser && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  Your account is missing fields the workflow needs (e.g. email). The submission may
                  fail server-side validation.
                </div>
              )}
              {metadataQuery.data.resource_type === 'pdf_survey' ? (
                <PdfSurveyForm
                  schema={metadataQuery.data}
                  uploadFile={uploadFile}
                  submitting={submitMutation.isPending}
                  submitLabel={event.display_title || 'Submit'}
                  serverError={serverError}
                  onCancel={onClose}
                  existingSubmissionFetcher={existingSubmissionFetcher}
                  externalIdentifiers={externalIdentifiers}
                  onSubmit={(payload: PdfSurveySubmitPayload) => {
                    const userRecord = user ? (user as unknown as Record<string, unknown>) : null;
                    const additions = buildLookupAdditions(
                      metadataQuery.data!,
                      entityId,
                      userRecord,
                    );
                    return submitMutation.mutateAsync({ ...payload, ...additions });
                  }}
                />
              ) : metadataQuery.data.resource_type === 'grid_input' ? (
                <GridInputForm
                  schema={metadataQuery.data}
                  entityId={entityId}
                  loadSubmissions={(formName, submissionId) =>
                    onboardingService.getGridSubmissions(formName, submissionId)
                  }
                  saveRow={(formName, body) => onboardingService.saveGridRow(formName, body)}
                  fetchDocumentVersion={(versionId) =>
                    onboardingService.getDocumentVersion({
                      entityType: 'case',
                      entityId,
                      versionId,
                    })
                  }
                  submitting={submitMutation.isPending}
                  submitLabel={event.display_title || 'Submit'}
                  serverError={serverError}
                  onCancel={onClose}
                  lookupAdditions={(() => {
                    const userRecord = user ? (user as unknown as Record<string, unknown>) : null;
                    return buildLookupAdditions(metadataQuery.data!, entityId, userRecord);
                  })()}
                  onSubmit={(payload: GridSubmitPayload) =>
                    submitMutation.mutateAsync(payload as FormSubmitValues)
                  }
                />
              ) : (
                <DynamicForm
                  schema={metadataQuery.data}
                  submitting={submitMutation.isPending}
                  submitLabel={event.display_title || 'Submit'}
                  serverError={serverError}
                  onSubmit={handleSubmit}
                  onCancel={onClose}
                  uploadFile={uploadFile}
                  documentFetcher={fetchDocumentVersion}
                  existingSubmissionFetcher={existingSubmissionFetcher}
                  externalIdentifiers={externalIdentifiers}
                />
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
